import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://blog.bozzam.com';
const SITE_NAME = 'Bozzam Blog';
const DEFAULT_IMAGE = `${SITE_URL}/logo-bozzam.png`;

const SEO = ({
  title,
  description,
  path = '',
  type = 'website',
  image,
  article,
  noindex = false,
  jsonLd
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Guides experts et simulateurs freelance 2026`;
  const fullUrl = `${SITE_URL}${path}`;
  const metaImage = image || DEFAULT_IMAGE;
  const metaDescription = description || 'Guides experts, simulateurs gratuits et articles de référence pour freelances et entrepreneurs. Données officielles 2026. URSSAF, fiscalité, statuts juridiques.';

  return (
    <Helmet>
      {/* Base */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Article-specific */}
      {article && <meta property="article:published_time" content={article.date} />}
      {article && <meta property="article:author" content={article.author || 'Bozzam'} />}
      {article?.tags?.map((tag, i) => (
        <meta key={i} property="article:tag" content={tag} />
      ))}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

// Helper: JSON-LD pour un article
export const articleJsonLd = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.excerpt,
  datePublished: article.date || article.publishedAt,
  dateModified: article.date || article.publishedAt,
  author: {
    '@type': 'Organization',
    name: 'Bozzam',
    url: 'https://bozzam.com'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Bozzam',
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo-bozzam.png` }
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/article/${article.slug}` }
});

// Helper: JSON-LD pour un simulateur
export const simulatorJsonLd = (sim) => ({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: sim.title,
  description: sim.description,
  applicationCategory: 'FinanceApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  provider: { '@type': 'Organization', name: 'Bozzam', url: 'https://bozzam.com' }
});

// Helper: JSON-LD BreadcrumbList
export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`
  }))
});

// JSON-LD Organization (homepage)
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Bozzam',
  url: 'https://bozzam.com',
  logo: `${SITE_URL}/logo-bozzam.png`,
  description: 'Plateforme tout-en-un pour freelances et entrepreneurs : coffre-fort numérique, missions ESN, synchronisation bancaire.',
  sameAs: [
    'https://www.linkedin.com/company/bozzam',
    'https://x.com/Bozzamfr',
    'https://www.facebook.com/profile.php?id=61581138358129',
    'https://www.instagram.com/bozzamfr/'
  ]
};

export default SEO;
